import { type Database } from '@/types/supabase'
import { v4 } from 'uuid'
import * as yup from 'yup'

export type NewReferenceInput = {
  new_reference_citation?: string
  new_reference_description?: string
}

type TableInsert =
  Database['public']['Tables'][keyof Database['public']['Tables']]['Insert']

export type InputTypeNoReferences<T extends TableInsert> = Omit<
  T,
  'species_id'
> & {
  taxon_name: string
}

export type InputType<T extends TableInsert> = InputTypeNoReferences<T> &
  NewReferenceInput

export type EmptyInputType<T extends TableInsert> = { [K in keyof T]: '' }

export type UploadType<T extends TableInsert> = Omit<
  T,
  'created_at' | 'created_by'
>
export type UploadWithNewReferenceType<T extends TableInsert> = T &
  NewReferenceInput & { taxon_name: string }

export const nullableStringValidator = yup
  .string()
  .trim()
  .nullable()
  .optional()
  .transform((value: string) => (value === '' ? null : value))

export const optionalStringValidator = yup
  .string()
  .trim()
  .optional()
  .transform((value: string) => (value === '' ? undefined : value))

export const numberValidator = yup
  .number()
  .transform((value, originalValue: string) => {
    if (originalValue === '') return null
    const parsedValue = Number(originalValue)
    if (isNaN(parsedValue)) {
      throw new Error(`Invalid number: ${originalValue}`)
    }
    return parsedValue
  })

export const nullableNumberValidator = numberValidator.nullable().optional()

export const commonFields = {
  id: yup
    .string()
    .uuid()
    .default(() => v4()),
  taxon_name: yup.string().required(),
  reference_id: nullableNumberValidator,
  new_reference_citation: optionalStringValidator,
  new_reference_description: optionalStringValidator,
  provenance_country: optionalStringValidator,
  provenance_detail: optionalStringValidator,
}
